import React, { useState } from 'react';
import { api } from '../lib/api.js';
import { ago, day, inr } from '../lib/format.js';
import { Alert, Card, CardSkeleton, Empty, Field, Pill, StatCard, useAsync } from '../components/ui.jsx';
import ExportButton from '../components/ExportButton.jsx';

const TONE = { good: 'issued', attention: 'pending', critical: 'cancelled' };
const TONE_LABEL = { good: 'healthy', attention: 'attention', critical: 'act now' };

/** Every workspace at a glance: paid up, connected to Tally, in use. */
export function WorkspaceHealth() {
  const h = useAsync(() => api.get('/api/admin/health'), []);
  const [only, setOnly] = useState('');
  if (h.loading && !h.data) return <CardSkeleton lines={6} />;
  if (h.error) return <Alert onRetry={h.reload}>{h.error}</Alert>;
  const all = h.data.workspaces;
  const rows = all.filter((w) => !only || w.health === only);
  const count = (k) => all.filter((w) => w.health === k).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Workspace health</h1>
          <div className="page-sub">
            Each client's subscription, Tally connection and use, read inside its own workspace. A problem here is
            usually a Tally computer that is off, or a renewal that is due.
          </div>
        </div>
        <div className="inline">
          <button className="btn secondary" onClick={h.reload}>Refresh</button>
          <ExportButton build={() => ({
            filename: 'workspace-health',
            title: 'Workspace health',
            sheets: [{
              name: 'Workspaces',
              columns: [
                { key: 'name', header: 'Workspace' }, { key: 'health_label', header: 'Health' },
                { key: 'problems_text', header: 'Problems' }, { key: 'access', header: 'Subscription' },
                { key: 'ends_on', header: 'Ends', type: 'date' }, { key: 'days_left', header: 'Days left', type: 'int' },
                { key: 'users', header: 'Users', type: 'int' }, { key: 'last_login', header: 'Last sign-in', type: 'date' },
                { key: 'agents', header: 'Agents', type: 'int' }, { key: 'agent_last_seen', header: 'Agent last seen', type: 'date' },
                { key: 'failed_jobs', header: 'Failed jobs', type: 'int' }, { key: 'unposted', header: 'Not in Tally', type: 'int' },
                { key: 'invoices_mtd', header: 'Invoices this month', type: 'int', total: true },
                { key: 'billed_mtd', header: 'Billed this month', type: 'money', total: true },
              ],
              rows: all.map((w) => ({ ...w, health_label: TONE_LABEL[w.health], problems_text: w.problems.join('; ') })),
            }],
          })} />
        </div>
      </div>

      <div className="grid kpi" style={{ marginBottom: 16 }}>
        <StatCard label="Workspaces" value={all.length} foot={`${all.filter((w) => w.access === 'active').length} in service`}
          onClick={() => setOnly('')} />
        <StatCard label="Act now" value={count('critical')} foot="agent offline, sync failing, or lapsed"
          tone={count('critical') ? 'attention' : undefined} onClick={() => setOnly(only === 'critical' ? '' : 'critical')} />
        <StatCard label="Attention" value={count('attention')} foot="renewal due, invoices waiting"
          onClick={() => setOnly(only === 'attention' ? '' : 'attention')} />
        <StatCard label="Billed this month" value={inr(all.reduce((a, w) => a + w.billed_mtd, 0), 0)}
          foot={`${all.reduce((a, w) => a + w.invoices_mtd, 0)} invoices across every workspace`} />
      </div>

      <Card pad={rows.length === 0}>
        {rows.length === 0 ? <Empty title="Nothing in this view" /> : (
          <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
            <table>
              <thead><tr><th>Workspace</th><th>Health</th><th>Subscription</th><th>Tally</th>
                <th className="r">This month</th><th>Last used</th></tr></thead>
              <tbody>
                {rows.map((w) => (
                  <tr key={w.id}>
                    <td>{w.name}<div className="sub mono">{w.slug} · {w.entities} compan{w.entities === 1 ? 'y' : 'ies'} · {w.users} users</div></td>
                    <td>
                      <Pill value={TONE[w.health]} label={TONE_LABEL[w.health]} />
                      {w.problems.length > 0 && <div className="sub" style={{ maxWidth: 260 }}>{w.problems.join(' · ')}</div>}
                    </td>
                    <td>
                      <Pill value={w.access === 'active' ? 'issued' : 'cancelled'} label={w.access} />
                      <div className="sub">{w.ends_on ? `ends ${day(w.ends_on)}${w.days_left !== null ? ` · ${w.days_left} d` : ''}` : 'open-ended'}</div>
                    </td>
                    <td>
                      {w.agents ? `${w.agents} agent${w.agents === 1 ? '' : 's'}` : <span className="muted">no agent</span>}
                      <div className="sub">
                        seen {ago(w.agent_last_seen)}{w.failed_jobs ? ` · ${w.failed_jobs} failed` : ''}
                        {w.queued_jobs ? ` · ${w.queued_jobs} queued` : ''}
                      </div>
                    </td>
                    <td className="r num">{inr(w.billed_mtd, 0)}<div className="sub">{w.invoices_mtd} invoices</div></td>
                    <td>{ago(w.last_login)}<div className="sub">last invoice {w.last_invoice ? ago(w.last_invoice) : 'never'}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

/** Who is due to renew, what they have been told, and where the notices go. */
export function Renewals() {
  const overview = useAsync(() => api.get('/api/admin/renewals'), []);
  const settings = useAsync(() => api.get('/api/admin/settings'), []);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [smtp, setSmtp] = useState({ user: '', pass: '' });
  const [running, setRunning] = useState(false);

  React.useEffect(() => {
    if (settings.data && !form) {
      const s = settings.data;
      setForm({
        notify_emails: s.notify_emails.join(', '), notify_webhook: s.notify_webhook || '',
        renewal_days: s.renewal_days.join(', '), remind_tenants: s.remind_tenants,
        host: s.smtp_config.host || '', port: s.smtp_config.port || 587, secure: Boolean(s.smtp_config.secure),
        from: s.smtp_config.from || '',
      });
    }
  }, [settings.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setError(null); setSaved(null);
    const days = form.renewal_days.split(',').map((x) => Number(x.trim())).filter((n) => Number.isInteger(n) && n >= 0);
    try {
      await api.put('/api/admin/settings', {
        notify_emails: form.notify_emails.split(',').map((x) => x.trim()).filter(Boolean),
        notify_webhook: form.notify_webhook.trim() || null,
        renewal_days: days,
        remind_tenants: form.remind_tenants,
        smtp_config: {
          ...(form.host.trim() ? { host: form.host.trim() } : {}),
          ...(String(form.port).trim() ? { port: Number(form.port) } : {}),
          secure: form.secure,
          ...(form.from.trim() ? { from: form.from.trim() } : {}),
        },
        ...(smtp.user && smtp.pass ? { smtp_secrets: smtp } : {}),
      });
      setSmtp({ user: '', pass: '' });
      setSaved('Saved.');
      settings.reload();
    } catch (e) { setError(e.message); }
  };

  const runNow = async () => {
    setRunning(true); setError(null); setSaved(null);
    try {
      const r = await api.post('/api/admin/renewals/run', {});
      setSaved(r.sent ? `${r.sent} reminder${r.sent === 1 ? '' : 's'} sent.` : 'Nothing was due, or no channel is set up to send it.');
      overview.reload();
    } catch (e) { setError(e.message); } finally { setRunning(false); }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Renewals</h1>
          <div className="page-sub">
            Subscriptions ending soon, and the notices sent about them. Each workspace is told once at each step
            before its end date; renewing starts the steps again.
          </div>
        </div>
        <button className="btn secondary" disabled={running} onClick={runNow}>{running ? 'Sending…' : 'Send due reminders now'}</button>
      </div>
      <Alert onRetry={() => setError(null)}>{error}</Alert>
      {saved && <Alert kind="ok">{saved}</Alert>}

      <Card title="Ending soon" note="From thirty days past to sixty days ahead">
        {!overview.data ? <CardSkeleton lines={3} /> : overview.data.upcoming.length === 0
          ? <Empty title="No subscription ends in the next sixty days" />
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Workspace</th><th>Ends</th><th className="r">Days</th><th>Last reminded</th></tr></thead>
                <tbody>
                  {overview.data.upcoming.map((u) => (
                    <tr key={u.id}>
                      <td>{u.name}</td>
                      <td>{day(u.ends_on)}</td>
                      <td className="r num" style={{ color: u.days_left <= 7 ? 'var(--critical-text)' : undefined }}>
                        {u.days_left < 0 ? `ended ${-u.days_left} d ago` : u.days_left}
                      </td>
                      <td>{u.last_reminded ? ago(u.last_reminded) : <span className="muted">not yet</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      {form && (
        <Card title="Where the notices go"
          note="By e-mail through the platform's own mail server, and to a webhook if one is set. Without either, nothing is sent and the console still lists what is due.">
          <div className="row">
            <Field label="Tell these addresses" hint="comma separated">
              <input value={form.notify_emails} placeholder="you@authentictechnologies.in"
                onChange={(e) => setForm({ ...form, notify_emails: e.target.value })} />
            </Field>
            <Field label="Also tell each workspace's administrators">
              <select value={String(form.remind_tenants)} onChange={(e) => setForm({ ...form, remind_tenants: e.target.value === 'true' })}>
                <option value="true">Yes</option><option value="false">No, only the platform</option>
              </select>
            </Field>
            <Field label="Days before the end" hint="comma separated">
              <input value={form.renewal_days} onChange={(e) => setForm({ ...form, renewal_days: e.target.value })} />
            </Field>
            <Field label="Webhook" hint="Slack, Teams or your own">
              <input value={form.notify_webhook} placeholder="https://hooks.example/invit"
                onChange={(e) => setForm({ ...form, notify_webhook: e.target.value })} />
            </Field>
          </div>
          <div className="row">
            <Field label="SMTP host"><input value={form.host} placeholder="smtp.example.com"
              onChange={(e) => setForm({ ...form, host: e.target.value })} /></Field>
            <Field label="Port"><input className="num" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} /></Field>
            <Field label="From address"><input value={form.from} placeholder="billing@authentictechnologies.in"
              onChange={(e) => setForm({ ...form, from: e.target.value })} /></Field>
            <Field label="Username" hint={settings.data.smtp_configured ? 'stored' : undefined}>
              <input value={smtp.user} autoComplete="off" placeholder={settings.data.smtp_configured ? '(leave blank to keep)' : ''}
                onChange={(e) => setSmtp({ ...smtp, user: e.target.value })} />
            </Field>
            <Field label="Password" hint={settings.data.smtp_configured ? 'stored' : undefined}>
              <input type="password" value={smtp.pass} autoComplete="new-password"
                placeholder={settings.data.smtp_configured ? '•••••••• (leave blank to keep)' : ''}
                onChange={(e) => setSmtp({ ...smtp, pass: e.target.value })} />
            </Field>
          </div>
          <button className="btn" onClick={save}>Save</button>
        </Card>
      )}

      {overview.data && overview.data.sent.length > 0 && (
        <Card title="Sent" note="Most recent first">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Workspace</th><th>For the end on</th><th className="r">Step</th><th>Sent</th><th>To</th></tr></thead>
              <tbody>
                {overview.data.sent.map((r) => (
                  <tr key={r.id}>
                    <td>{r.tenant_name}</td>
                    <td>{day(r.ends_on)}</td>
                    <td className="r num">{r.days_before} d</td>
                    <td>{ago(r.sent_at)} <span className="sub">by {r.channel}</span></td>
                    <td className="sub">{(r.recipients || []).join(', ') || '—'}{r.error ? ` · ${r.error}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}
